import { useCallback, useEffect, useState, lazy, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { Page } from "@boundary/layouts/pageLayout";
import { Spinner } from "@boundary/components/spinner";
import { useOrderFormState } from "@boundary/_hooks/useOrderFormState";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import styles from "./docView.module.scss";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const InspectFileContent = lazy(() =>
  import("@boundary/widgets/inspectFileContent").then((m) => ({
    default: m.InspectFileContent
  }))
);

const FILE_SEARCH_RETRY_DELAY = 500;

interface FileSearchResult {
  file: ReturnType<typeof useOrderFormState>["currentFile"];
  service: ReturnType<typeof useOrderFormState>["currentService"];
}

function findFileInServices(
  vm: ReturnType<typeof useOrderFormState>,
  fileId: string
): FileSearchResult | null {
  if (vm.currentService) {
    const file = vm.currentService.files.find(f => f.id === fileId);
    if (file) {
      return { file, service: vm.currentService };
    }
  }

  for (const service of vm.services) {
    const file = service.files.find(f => f.id === fileId);
    if (file) {
      return { file, service };
    }
  }

  return null;
}

export const DocViewPage = observer(function DocViewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const machineIdParam = searchParams.get("machineId");
  const fileIdParam = searchParams.get("fileId");
  const machineId = machineIdParam ? Number(machineIdParam) : 0;
  
  const vm = useOrderFormState(machineId);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!fileIdParam) return;

    const searchAndSetFile = () => {
      const result = findFileInServices(vm, fileIdParam);
      
      if (result?.file && result?.service) {
        runInAction(() => {
          vm.currentFile = result.file;
          vm.currentService = result.service;
        });
        vm.saveState();
        setIsInitialized(true);
        return true;
      }
      
      return false;
    };

    if (!searchAndSetFile()) {
        const timeoutId = setTimeout(() => {
        searchAndSetFile();
      }, FILE_SEARCH_RETRY_DELAY);
        
        return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileIdParam, machineId]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (!vm.currentFile?.previewUrl) {
    return (
      <Page screenBackground="sub">
        <Page.SafeArea />
        <Page.DisplayZone>
          <Page.DisplayZone.Header back onBackClick={handleBack} center={<h1 className={styles.headerTitle}>Документ</h1>} />
          <Page.DisplayZone.Body>
          <div className={styles.errorContainer}>
            <div className={styles.errorText}>Не удалось загрузить предпросмотр</div>
            <div className={styles.errorDetails}>
              {!vm.currentFile ? "Файл не найден" : "URL предпросмотра отсутствует"}
            </div>
          </div>
          </Page.DisplayZone.Body>
        </Page.DisplayZone>
      </Page>
    );
  }

  if (!isInitialized) {
    return (
      <Page screenBackground="sub">
        <Page.SafeArea />
        <Page.DisplayZone>
          <Page.DisplayZone.Header back onBackClick={handleBack} center={<h1 className={styles.headerTitle}>Документ</h1>} />
          <Page.DisplayZone.Body>
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
          </Page.DisplayZone.Body>
        </Page.DisplayZone>
      </Page>
    );
  }

  return (
    <Page screenBackground="sub">
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Header back onBackClick={handleBack} center={<h1 className={styles.headerTitle}>Документ</h1>} />
        <Page.DisplayZone.Body className={styles.content}>
        <Suspense fallback={
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
        }>
          <InspectFileContent file={vm.currentFile.previewUrl} />
        </Suspense>
        </Page.DisplayZone.Body>
      </Page.DisplayZone>
    </Page>
  );
});
